// Sourced statistics for /research. Every figure here was verified against the
// cited URL on 2026-08-14 (see scratchpad/stats-sources.md for the full dossier).
// RULE: nothing enters this file without a real, fetchable source. Stats that
// could not be traced to a primary live in UNVERIFIED below — published openly,
// because saying which numbers are folklore is the point of the page.
export const RESEARCH_UPDATED = 'August 14, 2026';

export const clusters = [
  {
    id: 'missed-calls',
    title: 'How many calls actually go unanswered',
    lede: 'The most-quoted number in this industry traces back to one 2015 study of 85 businesses. Here it is with its real date and sample size attached.',
    stats: [
      {
        figure: '37.8%',
        claim: 'of calls to small businesses were answered by a live person. The rest went to voicemail (37.8%) or rang out entirely (24.3%).',
        detail: 'A 30-day monitoring study of 85 small businesses across 58 industries. This is the origin of the widely recycled "62% of calls go unanswered" claim — usually re-dated to the current year without attribution.',
        source: '411 Locals call-monitoring study, conducted 2015, published January 2016',
        url: 'https://411locals.us/small-business-owners-dont-answer-62-of-phone-calls/',
      },
      {
        figure: '70%',
        claim: 'of the businesses in that same study answered fewer than half of their incoming calls.',
        source: '411 Locals, 2015',
        url: 'https://411locals.us/small-business-owners-dont-answer-62-of-phone-calls/',
      },
      {
        figure: '40%',
        claim: 'of US law firms answered a prospective client’s call — down from 56% in 2019. In total 48% neither answered nor called back.',
        detail: 'A third-party research firm contacted 500 US law firms posing as an interested client, June 20 – July 5, 2024. Only 33% responded to emails.',
        source: 'Clio, 2024 Legal Trends Report (secret-shopper study)',
        url: 'https://www.clio.com/blog/highlights-from-2024-legal-trends-report/',
      },
      {
        figure: '28%',
        claim: 'of business calls go unanswered on average, across the CallRail platform.',
        detail: 'Vendor telemetry from a call-tracking provider, not a methodology-published study — but far larger and more current than the 2015 figures above.',
        source: 'CallRail, reported by CMSWire (2026)',
        url: 'https://www.cmswire.com/the-wire/callrail-opens-voice-assist-to-every-business-now-with-contextual-ai-texting/',
      },
    ],
  },
  {
    id: 'speed-to-lead',
    title: 'Why answering fast matters more than answering well',
    lede: 'These are the two real speed-to-lead studies. Both are older than the blogs quoting them admit, and both measure something narrower than "conversions."',
    stats: [
      {
        figure: '42 hours',
        claim: 'was the average time US companies took to respond to a web lead. 23% never responded at all.',
        detail: 'An audit of 2,241 US companies. Only 37% responded within an hour.',
        source: 'Oldroyd, McElheran & Elkington, "The Short Life of Online Sales Leads," Harvard Business Review, March 2011',
        url: 'https://hbr.org/2011/03/the-short-life-of-online-sales-leads',
      },
      {
        figure: '~7x',
        claim: 'more likely to have a meaningful conversation with a decision-maker when a lead is contacted within the first hour — and 60x+ more likely than waiting 24 hours.',
        detail: 'Based on 1.25 million leads across 42 US companies. Important: this measures qualifying a lead, not closing a sale, and the comparison is hour one versus hour two.',
        source: 'Harvard Business Review, March 2011',
        url: 'https://hbr.org/2011/03/the-short-life-of-online-sales-leads',
      },
      {
        figure: '100x',
        claim: 'lower odds of reaching a lead when the first call happens at 30 minutes instead of 5. Odds of qualifying drop 21x.',
        detail: 'Covering 15,000+ web leads and 100,000+ call attempts over three years. The study explicitly states it did not address close ratios — vendor blogs claiming "21x more likely to convert" are misreading it. It is an InsideSales.com study co-authored by an MIT-affiliated professor, not "an MIT study."',
        source: 'Dr. James Oldroyd (MIT Sloan) with InsideSales.com, Lead Response Management Study, 2007',
        url: 'https://25649.fs1.hubspotusercontent-na2.net/hub/25649/file-13535879-pdf/docs/mit_study.pdf',
      },
    ],
  },
  {
    id: 'callbacks',
    title: 'Why calling them back doesn’t rescue the lead',
    lede: 'The quiet problem with missing a call: your callback comes from a number they don’t recognise.',
    stats: [
      {
        figure: '46%',
        claim: 'of unidentified calls go unanswered — even when a legitimate business is calling.',
        detail: 'Based on analysis of 221.3 billion calls plus a survey of 12,127 consumers. 92% of consumers believe unidentified calls are fraudulent.',
        source: 'Hiya, State of the Call 2024',
        url: 'https://www.hiya.com/state-of-the-call',
      },
      {
        figure: '60%',
        claim: 'of consumers say they never call back a number they missed if it is unidentified. 43% never answer unidentified calls at all.',
        source: 'Hiya, State of the Call 2024',
        url: 'https://www.hiya.com/state-of-the-call',
      },
      {
        figure: '36% vs 7%',
        claim: 'of consumers prefer a phone call over a text when dealing with healthcare providers — voice is still the preferred channel for sensitive matters.',
        source: 'Hiya, State of the Call 2024',
        url: 'https://www.hiya.com/state-of-the-call',
      },
    ],
  },
  {
    id: 'receptionist-cost',
    title: 'What a receptionist actually costs in Los Angeles',
    lede: 'The strongest cluster on this page: all of it is US government data.',
    la: true,
    stats: [
      {
        figure: '$20.81/hr',
        claim: 'is the median receptionist wage in the Los Angeles–Long Beach–Anaheim metro — $43,280 a year before benefits. California statewide: $21.20/hr.',
        source: 'US Bureau of Labor Statistics, OEWS May 2025 (via O*NET, DOL-sponsored)',
        url: 'https://www.onetonline.org/link/localwages/43-4171.00?st=CA',
      },
      {
        figure: '$18.27/hr',
        claim: 'is the national median receptionist wage — $38,010 a year. The occupation employs about 1,007,200 people.',
        source: 'US Bureau of Labor Statistics, OEWS May 2025',
        url: 'https://www.bls.gov/oes/current/oes434171.htm',
      },
      {
        figure: '30.1%',
        claim: 'of total employer compensation is benefits, not wages — $14.01/hour on top of $32.60 in wages for private-industry workers.',
        source: 'BLS, Employer Costs for Employee Compensation, March 2026',
        url: 'https://www.bls.gov/news.release/ecec.nr0.htm',
      },
      {
        figure: '$56K–$62K',
        claim: 'is roughly what one full-time LA receptionist costs per year, fully loaded — and covers 40 of the week’s 168 hours.',
        detail: 'This is our arithmetic, not a BLS figure: the $43,280 LA median wage plus the BLS private-industry benefits load (benefits run about 43% of wages, lower at lower wage percentiles). Before overtime, PTO backfill, coverage gaps, or turnover.',
        ours: true,
        source: 'UBOTIKA calculation from BLS wage and benefit data above',
        url: 'https://www.bls.gov/news.release/ecec.nr0.htm',
      },
    ],
  },
  {
    id: 'ai-sentiment',
    title: 'What people actually think about AI answering the phone',
    lede: 'Including the numbers that cut against us. An AI receptionist has a bar to clear, and pretending otherwise would make everything else here less believable.',
    stats: [
      {
        figure: '64%',
        claim: 'of customers would prefer that companies did not use AI for customer service. 53% would consider switching to a competitor over it.',
        detail: 'Their top fear: 60% worry AI will make it harder to reach a human. 42% worry it will give wrong answers. Survey of 5,728 customers. This is the bar — instant pickup, and a clean path to a person.',
        against: true,
        source: 'Gartner survey, December 2023 (published July 2024)',
        url: 'https://www.gartner.com/en/newsroom/press-releases/2024-07-09-gartner-survey-finds-64-percent-of-customers-would-prefer-that-companies-didnt-use-ai-for-customer-service',
      },
      {
        figure: '51%',
        claim: 'of consumers prefer interacting with a bot over a human when they want immediate service.',
        detail: 'Immediacy is the one thing callers consistently reward AI for.',
        source: 'Zendesk CX Trends research',
        url: 'https://www.zendesk.com/blog/ai-customer-service-statistics/',
      },
      {
        figure: '58%',
        claim: 'of US small businesses now use generative AI — up from 40% in 2024 and 23% in 2023.',
        source: 'US Chamber of Commerce Technology Engagement Center with Teneo Research, August 2025',
        url: 'https://www.uschamber.com/technology/empowering-small-business-the-impact-of-technology-on-u-s-small-business',
      },
    ],
  },
  {
    id: 'bilingual-la',
    title: 'The bilingual math in Los Angeles',
    lede: 'If your phone only works in English, this is the share of the county it works less well for.',
    la: true,
    stats: [
      {
        figure: '37.6%',
        claim: 'of LA County residents aged 5+ speak Spanish at home — about 3.5 million people. 55.2% speak a language other than English at home.',
        source: 'US Census Bureau, American Community Survey 2024 1-year estimates',
        url: 'https://data.census.gov/table/ACSDT1Y2024.C16001?g=050XX00US06037',
      },
      {
        figure: '76%',
        claim: 'of consumers prefer to buy with product information in their own language. 75% are more likely to buy again when customer care is in their language.',
        detail: 'Survey of 8,709 consumers across 29 countries. 40% say they will never buy from websites in another language.',
        source: 'CSA Research, "Can’t Read, Won’t Buy – B2C," July 2020',
        url: 'https://csa-research.com/l/media/Consumers-Prefer-their-Own-Language',
      },
    ],
  },
];

// The point of publishing these: every one appears on competitors' pages as fact.
export const UNVERIFIED = [
  {
    claim: '"85% of callers who don’t reach a business never call back"',
    finding: 'No primary source exists. It circulates blog-to-blog with no study, sample size, or year ever named.',
  },
  {
    claim: '"80% of callers sent to voicemail don’t leave a message"',
    finding: 'Apocryphal. No traceable original study, despite appearing on hundreds of vendor pages.',
  },
  {
    claim: '"78% of customers buy from the company that responds first"',
    finding: 'Universally attributed to a "Lead Connect survey" that has no published report, methodology, sample size, or date.',
  },
  {
    claim: '"The average small business loses $126,000 a year to missed calls"',
    finding: 'Could not be traced to any study. The figure is presented as universal despite obviously depending on industry and deal size.',
  },
  {
    claim: '"X% of calls arrive outside business hours"',
    finding: 'Circulating figures range from 25% to 65% across vendor blogs, none with a fetchable primary. The spread itself shows the number is being invented.',
  },
  {
    claim: '"Dental practices miss 20–35% of calls"',
    finding: 'Secondary sources only — repeated by vendors citing each other, no underlying study located.',
  },
];

export const totalStats = clusters.reduce((n, c) => n + c.stats.length, 0);
